import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';

import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import { useTheme } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import Dialog from "components/dialog";
import ErrorSnackbarContent from "components/error-snackbar-content";
import {
  useGetAmbientScribeFeedbackQuery,
  useSubmitAmbientScribeFeedbackMutation
} from "services/ambient-scribe";

import {
  closeScribeFeedbackDialog,
  selectScribeFeedbackDialog
} from "../medical-scribe.slice";
import LoadingButton from "@mui/lab/LoadingButton";

import { feedbackThumbDownRating, feedbackThumbUpRating, MEDICAL_SCRIBE_FEEDBACK_TYPES } from "../constants";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { misc } from "app/themes/colors";

const dialogContentProps = {
  sx: {
    display: "flex",
    alignItems: "center",
    pb: "20px",
    flexDirection: "column"
  }
};

export default function MedicalScribeFeedbackDialog() {

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { open, scribeId, feedbackType, rating } =
    useAppSelector(selectScribeFeedbackDialog) || {};

  const [submitAmbientScribeFeedback, { isLoading: isUploadingFeedback }] =
    useSubmitAmbientScribeFeedbackMutation();

    const [note, setNote] = useState("");

    const [selectedRating, setSelectedRating] = useState<number | undefined>(rating);

    useEffect(()=>{
      setSelectedRating(rating);
    }, [rating])

    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(()=>{
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, [inputRef]);


  const { data: feedback = [], isFetching } = useGetAmbientScribeFeedbackQuery(
    Number(scribeId),
    {
      skip: !open || !scribeId
    }
  );

  useEffect(()=>{
      if (open) {
        setNote(feedback.find((feedback) => feedback.feedback_type === feedbackType)?.note || "")

      }else {
        setNote("");
      }
    }, [open, feedback, feedbackType]);

  const handleClose = useCallback(() => {
    dispatch(closeScribeFeedbackDialog());
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    const response = await submitAmbientScribeFeedback({
      scribe_id: Number(scribeId),
      feedback_type: Number(feedbackType),
      rating: Number(selectedRating),
      note
    });

    if ("error" in response) {
      dispatch(setCustomMessage(<ErrorSnackbarContent response={response} />));
      dispatch(setOpen(true));
    } else {
      dispatch(setCustomMessage("Feedback submitted successfully"));
      dispatch(setOpen(true));
    }
    handleClose();
  }, [submitAmbientScribeFeedback, scribeId, feedbackType, selectedRating, note, handleClose, dispatch]);



  const upButton = useMemo(() => {
    const isUp = selectedRating === feedbackThumbUpRating;
    return <IconButton
    onClick={()=>{
      setSelectedRating(feedbackThumbUpRating)
      inputRef.current?.focus();
    }}
                sx={{ width: "60px", height: "60px", border: "1px solid", borderColor: "divider",

                background:  isUp ? misc.subMenuItem : "transparent" }}>
        {isUp ? <ThumbUpIcon sx={{fontSize:"32px", color: misc.suno}} /> : <ThumbUpOffAltIcon sx={{fontSize:"32px", color: misc.suno}} />}

        </IconButton>
  }, [selectedRating]);

  const downButton = useMemo(() => {
    const isDown = selectedRating === feedbackThumbDownRating;
    return <IconButton
    onClick={()=>{
      setSelectedRating(feedbackThumbDownRating);
      inputRef.current?.focus();
    }}
                sx={{ width: "60px", height: "60px",
                   border: "1px solid", borderColor: "divider",

                background:  isDown ? misc.subMenuItem : "transparent" }}>
        {isDown ? <ThumbDownIcon sx={{color: misc.suno, fontSize:"32px"}} /> : <ThumbDownOffAltIcon sx={{fontSize:"32px", color: misc.suno}} />}

        </IconButton>
  }, [selectedRating]);

  return (
    <Dialog
      title={`Send ${MEDICAL_SCRIBE_FEEDBACK_TYPES.getFromCode(feedbackType)} Feedback to Suno Team`}
      onClose={handleClose}
      open={Boolean(open)}
      dialogContentProps={dialogContentProps}
      sx={{ zIndex: theme.zIndex.snackbar + 101 }}
    >
      <Stack direction="row" alignItems="center" columnGap={2} pr={2} mb={3} >
        {upButton}
        {downButton}
      </Stack>
      <Box width="100%" mb={3}>
        <TextField
        autoFocus
        inputRef={inputRef}
          fullWidth
          label="Share more and help us improve"
          multiline
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Share more and help us improve"
        />

      </Box>
      <DialogActions>
        <Button
          data-testid="scribe-feedback-dialog-cancel"
          variant="outlined"
          size="small"
          onClick={handleClose}
        >
          Cancel
        </Button>

        <LoadingButton
          loading={isUploadingFeedback}
          data-testid="scribe-feedback-dialog-submit"
          variant="contained"
          size="small"
          disabled={isFetching}
          onClick={handleSubmit}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
